import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { colors, spacing, typography } = themeVariables;

/**
 * Width at which the block is wide enough to seat two partners side by side, and
 * to earn the wider gutter. A container width, not a viewport one: the design
 * system ships no breakpoint tokens, and the block reflows against the space it is
 * given rather than the screen. 768px is where it has always gone two-up.
 */
const TWO_UP_WIDTH = 768;

/**
 * The tinted panel runs the full width of the block, but its contents line up with
 * every other section on the page, so the width and gutters are applied to the
 * content rather than to the panel. Matches the site content container the Ribbons
 * version got from `maxWidthContainer`.
 */
const CONTENT_MAX_WIDTH = 1440;

const content = css`
  max-width: ${CONTENT_MAX_WIDTH}px;
  margin-inline: auto;
  padding-inline: var(${spacing.md});

  @container partner-block (min-width: ${TWO_UP_WIDTH}px) {
    padding-inline: var(${spacing.lg});
  }
`;

/**
 * The partners overlap the top edge of the tinted panel rather than sitting inside
 * it. The intro's bottom margin carries the same offset so the overlap costs no
 * vertical space.
 */
const PANEL_OVERLAP = spacing.lg;

export const block = css`
  container: partner-block / inline-size;
`;

export const header = css`
  ${content}
`;

export const heading = css`
  margin: 0 0 var(${spacing.md}) 0;
  font-family: var(${typography.heading.fontFamily});
  font-weight: var(${typography.heading.fontWeight});
  font-size: var(${typography.heading.sizes.xs.fontSize});
  line-height: var(${typography.heading.sizes.xs.lineHeight});
  letter-spacing: var(${typography.heading.letterSpacing});
  color: var(${colors.content.default});

  /* Ranged left until there is room for two partners, then centred over them. */
  @container partner-block (min-width: ${TWO_UP_WIDTH}px) {
    text-align: center;
  }
`;

export const copy = css`
  margin: 0 0 calc(var(${spacing.xl}) + var(${PANEL_OVERLAP})) 0;
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.md.fontSize});
  line-height: var(${typography.body.sizes.md.lineHeight});
  color: var(${colors.content.default});

  @container partner-block (min-width: ${TWO_UP_WIDTH}px) {
    /* Steps up with the heading, as it did before, so the intro does not thin out
       into a wide column of small text. */
    font-size: var(${typography.body.sizes.lg.fontSize});
    line-height: var(${typography.body.sizes.lg.lineHeight});
    text-align: center;
  }
`;

export const panel = css`
  padding-bottom: var(${spacing.xl});
  background-color: var(${colors.surface.brand});
`;

export const partners = css`
  ${content}
  display: grid;
  grid-template-columns: 1fr;
  gap: var(${spacing.xxl});
  /* Block only: the inline margins are auto, set by content, and centre the list. */
  margin-block: 0;
  transform: translateY(calc(var(${PANEL_OVERLAP}) * -1));
  list-style: none;

  @container partner-block (min-width: ${TWO_UP_WIDTH}px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const partnerItem = css`
  /* Stacked one above the other, the partners need a line between them to read as
     separate. Side by side they do not, and the gap does that job. */
  padding-bottom: var(${spacing.xxl});
  border-bottom: 1px solid var(${colors.stroke.default});

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }

  @container partner-block (min-width: ${TWO_UP_WIDTH}px) {
    padding-bottom: 0;
    border-bottom: none;
  }
`;
